import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

declare const $: any;
import { NotificationService } from 'app/services/notification.service';
import { FileStatus } from 'app/models/file-upload/upload-file-with-status';
import { UtilityService } from 'app/services/utility.service';
import { ZipContentValidator } from 'app/shared/utils/zip-content-validator.util';

@Component({
	selector: 'client-purchasing-file-upload',
	templateUrl: './file-upload.component.html',
	styleUrls: ['./file-upload.component.css'],
	standalone: false,
})
export class FileUploadComponent implements AfterViewInit {
	@ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement>;

	@Input() labelName: string;
	@Input() isRequired: boolean;
	@Input() submitted: boolean;
	@Input() isDisabled = false;
	@Input() initialFile: File | null = null;
	@Output() readonly fileValueChanged: EventEmitter<any> = new EventEmitter<any>();
	@Output() readonly fileSizeExceeded: EventEmitter<void> = new EventEmitter<void>();
	@Input() isValid: boolean;
	@Input() allowedFileType: string = UtilityService.ALLOWED_FILE_EXTENSIONS.join(',');
	@Input() fileUploadStatus: FileStatus | null = null;
	@Output() readonly retry: EventEmitter<void> = new EventEmitter<void>();
	constructor(private readonly notificationService: NotificationService) {}

	ngAfterViewInit(): void {
		if (this.initialFile && this.fileInputRef?.nativeElement) {
			const $container = $(this.fileInputRef.nativeElement).closest('.fileinput');
			$container.removeClass('fileinput-new').addClass('fileinput-exists');
			$container.find('.fileinput-filename').text(this.initialFile.name);
		}
	}

	async onFileChange($event: Event) {
		const input = $event?.target as HTMLInputElement;
		let file = null;

		if (input?.files?.length) {
			file = input.files[0];

			if (!this.isValidFileType(file)) {
				const allowedTypes = this.getAllowedFileTypesDisplay();
				this.notificationService.showError(`Invalid file type. Please select a file of type: ${allowedTypes}`);
				this.resetFileInput();
				return;
			}

			const fileSizeInMB = file.size / (1024 * 1024);
			const maxSizeMB = 50;

			if (file.size > 50 * 1024 * 1024) {
				this.notificationService.showError(
					`The file you selected is ${fileSizeInMB.toFixed(2)} MB. Maximum allowed size is ${maxSizeMB} MB.`
				);
				this.fileSizeExceeded.emit();

				this.resetFileInput();
				return;
			}

			if (await this.hasRestrictedZipContent(file)) {
				this.resetFileInput();
				return;
			}
		}

		this.fileValueChanged.emit(file);
	}

	private async hasRestrictedZipContent(file: File): Promise<boolean> {
		if (!(file?.name ?? '').toLowerCase().endsWith('.zip')) {
			return false;
		}
		const result = await ZipContentValidator.inspect(file);
		if (!result.allowed) {
			const restrictedNames = result.restrictedEntries.map((entry) => entry.name).join(', ');
			this.notificationService.showError(
				`The file "${file.name}" contains restricted file types that are not allowed: ${restrictedNames}. ` +
					'ZIP archives must not contain executable or script files.'
			);
			return true;
		}
		return false;
	}

	resetFileInput(): void {
		if (this.fileInputRef?.nativeElement) {
			this.fileInputRef.nativeElement.value = '';
		}
	}

	restoreDisplay(file: File | null): void {
		if (!this.fileInputRef?.nativeElement) return;
		this.fileInputRef.nativeElement.value = '';
		const $container = $(this.fileInputRef.nativeElement).closest('.fileinput');
		if (file) {
			$container.removeClass('fileinput-new').addClass('fileinput-exists');
			$container.find('.fileinput-filename').text(file.name);
		} else {
			$container.removeClass('fileinput-exists').addClass('fileinput-new');
			$container.find('.fileinput-filename').text('');
		}
	}

	reset(): void {
		if (this.fileInputRef?.nativeElement) {
			this.fileInputRef.nativeElement.value = '';
			const $container = $(this.fileInputRef.nativeElement).closest('.fileinput');
			$container.removeClass('fileinput-exists').addClass('fileinput-new');
			$container.find('.fileinput-filename').text('');
		}
		this.fileValueChanged.emit(null);
	}

	private isValidFileType(file: File): boolean {
		if (!this.allowedFileType) {
			return true;
		}

		const allowedExts = this.allowedFileType
			.split(',')
			.map((t) => t.trim().toLowerCase())
			.filter((t) => t.startsWith('.'));
		const ext = '.' + file.name.split('.').pop()?.toLowerCase();

		if (!allowedExts.includes(ext)) {
			return false;
		}

		const mimeType = file.type.toLowerCase();
		if (!mimeType || mimeType === 'application/octet-stream') {
			return true;
		}

		const allowedMimes = UtilityService.EXTENSION_MIME_MAP[ext] ?? [];
		return allowedMimes.length === 0 || allowedMimes.includes(mimeType);
	}

	private getAllowedFileTypesDisplay(): string {
		if (!this.allowedFileType) {
			return '';
		}

		const allowedTypes = this.allowedFileType.split(',').map((type) => type.trim());
		const extensions = allowedTypes.filter((type) => type.startsWith('.')).map((type) => type.toUpperCase());

		return extensions.length > 0 ? extensions.join(', ') : 'specified formats';
	}
	onRetry(): void {
		this.retry.emit();
	}
}
